import React from 'react';
import { Page, Crumbs, Card, Btn, Pill, DataTable, StatCard, ui, type Col } from '../shared/ui';

type HedgingModuleViewProps = {
    payload: any;
    locale?: 'hr' | 'en';
    token?: string | null;
    formatMoney: (value: number, locale?: 'hr' | 'en') => string;
    formatNumber: (value: number, locale?: 'hr' | 'en') => string;
    formatDate: (value: string, locale?: 'hr' | 'en') => string;
    LineChart: React.ComponentType<{ points: Array<{ date: string; net: number }>; projection?: Array<{ date: string; net: number }> }>;
};

export function HedgingModuleView({ payload, locale = 'hr', token, formatMoney, formatNumber, formatDate, LineChart }: HedgingModuleViewProps) {
    const [autoOn, setAutoOn] = React.useState<boolean>(payload?.automation?.enabled ?? false);
    const [autoToggling, setAutoToggling] = React.useState(false);
    const [changedBy, setChangedBy] = React.useState<string | null>(payload?.automation?.changed_by ?? null);
    const [changedAt, setChangedAt] = React.useState<string | null>(payload?.automation?.changed_at ?? null);
    React.useEffect(() => {
        setAutoOn(payload?.automation?.enabled ?? false);
        setChangedBy(payload?.automation?.changed_by ?? null);
        setChangedAt(payload?.automation?.changed_at ?? null);
    }, [payload?.automation?.enabled, payload?.automation?.changed_by, payload?.automation?.changed_at]);

    // Backlog: nehedgirane ponude od zadnjeg gašenja automatike (checkbox lista pri uključivanju).
    const [backlog, setBacklog] = React.useState<{ rows: any[]; count: number; total_oz: number; since: string } | null>(null);
    const [selected, setSelected] = React.useState<Set<number>>(new Set());
    const [backlogBusy, setBacklogBusy] = React.useState(false);
    const [backlogMsg, setBacklogMsg] = React.useState('');

    const fetchBacklog = React.useCallback(async () => {
        try {
            const r = await fetch('/api/hedge/automation/backlog', { headers: { Authorization: `Bearer ${token ?? ''}` } });
            if (!r.ok) return;
            const d = await r.json();
            setBacklog(d);
            setSelected(new Set<number>((d.rows ?? []).map((x: any) => Number(x.id))));
        } catch { /* ignore */ }
    }, [token]);

    const toggleAuto = React.useCallback(async () => {
        setAutoToggling(true);
        setBacklogMsg('');
        try {
            const next = !autoOn;
            const r = await fetch('/api/hedge/automation/toggle', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
                body: JSON.stringify({ enabled: next }),
            });
            if (r.ok) {
                const d = await r.json();
                setAutoOn(!!d.enabled);
                setChangedBy(locale === 'en' ? 'you' : 'ti'); setChangedAt(new Date().toISOString());
                if (d.enabled) { await fetchBacklog(); } else { setBacklog(null); }
            }
        } catch { /* ignore */ } finally { setAutoToggling(false); }
    }, [autoOn, token, locale, fetchBacklog]);

    const hedgeSelected = React.useCallback(async () => {
        if (selected.size === 0) return;
        setBacklogBusy(true); setBacklogMsg('');
        try {
            const r = await fetch('/api/hedge/automation/hedge-selected', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
                body: JSON.stringify({ ids: [...selected] }),
            });
            const d = await r.json();
            if (r.ok && d.ok) { setBacklogMsg((locale === 'en' ? 'Hedged ' : 'Hedgirano ') + `${d.hedged_count} (${d.oz} oz · ${d.pair})`); await fetchBacklog(); }
            else { setBacklogMsg(d.error || 'Greška'); }
        } catch (e) { setBacklogMsg(String(e)); } finally { setBacklogBusy(false); }
    }, [selected, token, locale, fetchBacklog]);

    if (!payload) {
        return <section className="card">Učitavam hedging...</section>;
    }

    const recent = payload.recent_deals.slice(0, 20);
    const portfolioDaily = [...(payload.portfolio_daily ?? [])];
    const dealsDailyByDate = new Map<string, any>((payload.deals_daily ?? []).map((row: any) => [row.date, row]));
    const exposureSign = payload.exposure.gap_amount >= 0 ? '+' : '-';
    const coveragePct = Math.max(0, Math.min(200, Number(payload.exposure.coverage_pct ?? 0)));
    const coverageNeedle = Math.max(0, Math.min(100, Math.round((coveragePct / 200) * 100)));
    const ozToKg = (oz: number): number => oz * 0.0311034768;
    const formatCompactMarket = (value: number): string => {
        const abs = Math.abs(value);
        if (abs >= 1_000_000) {
            return `${(value / 1_000_000).toFixed(2)} M`;
        }
        return formatNumber(value, locale);
    };
    const portfolioScale = (() => {
        const maxAbs = Math.max(0, ...portfolioDaily.map((row: any) => Math.abs(Number(row.change_value ?? 0))));
        return maxAbs > 5_000_000 ? 1000 : 1;
    })();
    const dailyRows = (portfolioDaily.length > 0 ? portfolioDaily : (payload.deals_daily ?? []).map((row: any) => ({
        date: row.date,
        change_value: row.net ?? 0,
    }))).map((row: any) => {
        const deal = dealsDailyByDate.get(row.date) || { buy_amount: 0, sell_amount: 0, quantity_oz: 0, net: 0 };
        return {
            date: row.date,
            pnl: Number(row.change_value ?? deal.net ?? 0) / portfolioScale,
            buy_amount: Number(deal.buy_amount ?? 0),
            sell_amount: Number(deal.sell_amount ?? 0),
            qty_oz: Number(deal.quantity_oz ?? 0),
        };
    });
    const topGainDays = [...dailyRows].filter((row: any) => row.pnl > 0).sort((a: any, b: any) => b.pnl - a.pnl).slice(0, 10);
    const topLossDays = [...dailyRows].filter((row: any) => row.pnl < 0).sort((a: any, b: any) => a.pnl - b.pnl).slice(0, 10);

    const monthlyMap = new Map<string, {
        period: string;
        buyDeals: number;
        sellDeals: number;
        buyQty: number;
        sellQty: number;
        buyAmount: number;
        sellAmount: number;
    }>();
    payload.monthly_book.forEach((row: any) => {
        if (!monthlyMap.has(row.period)) {
            monthlyMap.set(row.period, {
                period: row.period,
                buyDeals: 0,
                sellDeals: 0,
                buyQty: 0,
                sellQty: 0,
                buyAmount: 0,
                sellAmount: 0,
            });
        }
        const target = monthlyMap.get(row.period)!;
        if (row.deal_type === 'BUY') {
            target.buyDeals = row.deals;
            target.buyQty = row.quantity_oz;
            target.buyAmount = row.amount;
        } else {
            target.sellDeals = row.deals;
            target.sellQty = row.quantity_oz;
            target.sellAmount = row.amount;
        }
    });
    const monthlyRows = [...monthlyMap.values()].sort((a, b) => a.period.localeCompare(b.period));

    const pairRows = (() => {
        const map = new Map<string, { pair: string; buyAmount: number; sellAmount: number; buyQty: number; sellQty: number; buyCount: number; sellCount: number }>();
        payload.deals_by_pair.forEach((row: any) => {
            if (!map.has(row.pair)) {
                map.set(row.pair, { pair: row.pair, buyAmount: 0, sellAmount: 0, buyQty: 0, sellQty: 0, buyCount: 0, sellCount: 0 });
            }
            const target = map.get(row.pair)!;
            if (row.deal_type === 'BUY') {
                target.buyAmount = row.amount;
                target.buyQty = row.quantity_oz;
                target.buyCount = row.count;
            } else {
                target.sellAmount = row.amount;
                target.sellQty = row.quantity_oz;
                target.sellCount = row.count;
            }
        });
        return [...map.values()];
    })();

    const monthPl = payload.portfolio.map((row: any) => ({ label: row.period.slice(0, 7), net: row.net_pl }));
    const maxAbsPl = Math.max(1, ...monthPl.map((row: any) => Math.abs(row.net)));
    const ozStart = payload.portfolio.map((row: any) => ({ date: row.period, net: row.opening_xau_oz }));
    const ozEnd = payload.portfolio.map((row: any) => ({ date: row.period, net: row.closing_xau_oz }));

    const isHrms = typeof document !== 'undefined' && document.body.dataset.theme === 'hrms';

    // ── Moderni HRMS UI-kit prikaz (gate isHrms) — legacy gold ostaje netaknut u else grani ──
    if (isHrms) {
        const monthlyCols: Col<{ id: string; period: string; buyDeals: number; buyQty: number; buyAmount: number; sellDeals: number; sellQty: number; sellAmount: number }>[] = [
            { key: 'period', label: locale === 'en' ? 'Month' : 'Mjesec', render: (r) => <strong style={{ color: ui.ink }}>{r.period}</strong> },
            { key: 'buyDeals', label: 'BUY', align: 'right', render: (r) => formatNumber(r.buyDeals, locale) },
            { key: 'buyQty', label: 'BUY OZ', align: 'right', render: (r) => formatNumber(r.buyQty, locale) },
            { key: 'buyAmount', label: 'BUY EUR', align: 'right', render: (r) => formatMoney(r.buyAmount, locale) },
            { key: 'sellDeals', label: 'SELL', align: 'right', render: (r) => formatNumber(r.sellDeals, locale) },
            { key: 'sellQty', label: 'SELL OZ', align: 'right', render: (r) => formatNumber(r.sellQty, locale) },
            { key: 'sellAmount', label: 'SELL EUR', align: 'right', render: (r) => formatMoney(r.sellAmount, locale) },
        ];
        const pairCols: Col<{ id: string; pair: string; buyCount: number; buyQty: number; buyAmount: number; sellCount: number; sellQty: number; sellAmount: number }>[] = [
            { key: 'pair', label: 'Pair', render: (r) => <strong style={{ color: ui.ink }}>{r.pair}</strong> },
            { key: 'buyCount', label: 'BUY cnt', align: 'right', render: (r) => formatNumber(r.buyCount, locale) },
            { key: 'buyQty', label: 'BUY OZ', align: 'right', render: (r) => formatNumber(r.buyQty, locale) },
            { key: 'buyAmount', label: 'BUY EUR', align: 'right', render: (r) => formatMoney(r.buyAmount, locale) },
            { key: 'sellCount', label: 'SELL cnt', align: 'right', render: (r) => formatNumber(r.sellCount, locale) },
            { key: 'sellQty', label: 'SELL OZ', align: 'right', render: (r) => formatNumber(r.sellQty, locale) },
            { key: 'sellAmount', label: 'SELL EUR', align: 'right', render: (r) => formatMoney(r.sellAmount, locale) },
        ];
        const dayCols = (tone: 'pos' | 'neg'): Col<{ id: string; date: string; pnl: number; qty_oz: number; sell_amount: number; buy_amount: number }>[] => [
            { key: 'date', label: locale === 'en' ? 'Date' : 'Datum', render: (r) => formatDate(r.date, locale) },
            { key: 'pnl', label: 'P&L €', align: 'right', render: (r) => <strong style={{ color: tone === 'neg' ? '#c0264a' : '#1a7a40' }}>{formatCompactMarket(r.pnl)}</strong> },
            { key: 'qty', label: 'KG', align: 'right', render: (r) => formatNumber(ozToKg(r.qty_oz), locale) },
            { key: 'sell', label: 'SELL €', align: 'right', render: (r) => formatCompactMarket(r.sell_amount) },
            { key: 'buy', label: 'BUY €', align: 'right', render: (r) => formatCompactMarket(r.buy_amount) },
        ];
        const recentCols: Col<{ id: string; date: string; doc: string; type: string; pair: string; amount: number }>[] = [
            { key: 'date', label: locale === 'en' ? 'Date' : 'Datum', render: (r) => formatDate(r.date, locale) },
            { key: 'doc', label: locale === 'en' ? 'Doc' : 'Dokument', render: (r) => <span style={{ fontFamily: 'monospace', color: ui.ink2 }}>{r.doc}</span> },
            { key: 'type', label: locale === 'en' ? 'Type' : 'Tip', render: (r) => <Pill tone={r.type === 'BUY' ? 'ok' : 'info'}>{r.type}</Pill> },
            { key: 'pair', label: locale === 'en' ? 'Pair' : 'Par', render: (r) => r.pair },
            { key: 'amount', label: locale === 'en' ? 'Amount' : 'Iznos', align: 'right', render: (r) => <strong>{formatMoney(r.amount, locale)}</strong> },
        ];

        return (
            <Page>
                <Crumbs items={[locale === 'en' ? 'Dashboard' : 'Dashboard', locale === 'en' ? 'Hedging & trading' : 'Hedging i trading']} />

                {payload.automation?.can_toggle ? (
                    <Card pad={18} style={{ borderLeft: `4px solid ${autoOn ? '#1a7a40' : '#c0264a'}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? 'Automatic hedging' : 'Automatsko hedgiranje'}</div>
                                <strong style={{ fontSize: '1.05rem', color: autoOn ? '#1a7a40' : ui.ink }}>
                                    {autoOn
                                        ? (locale === 'en' ? '📡 ON — auto-hedge every 15 min' : '📡 UKLJUČENO — auto-hedge svakih 15 min')
                                        : (locale === 'en' ? '✋ OFF — manual' : '✋ ISKLJUČENO — ručno')}
                                </strong>
                                {changedBy ? (
                                    <div style={{ fontSize: '0.78rem', color: ui.muted, marginTop: 2 }}>
                                        {autoOn ? (locale === 'en' ? 'Turned on by ' : 'Uključio/la: ') : (locale === 'en' ? 'Last changed by ' : 'Zadnje mijenjao/la: ')}
                                        <strong>{changedBy}</strong>{changedAt ? ` · ${formatDate(changedAt, locale)} ${String(changedAt).replace('T', ' ').slice(11, 16)}` : ''}
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <Btn variant="soft" onClick={fetchBacklog}>🔍 {locale === 'en' ? 'Non-hedged' : 'Nehedgirane'}</Btn>
                                <Btn variant={autoOn ? 'danger' : 'primary'} disabled={autoToggling} onClick={toggleAuto}>
                                    {autoToggling ? '…' : autoOn ? (locale === 'en' ? 'Turn OFF' : 'Isključi') : (locale === 'en' ? 'Turn ON' : 'Uključi')}
                                </Btn>
                            </div>
                        </div>
                        {backlog ? (
                            <div style={{ marginTop: 12, borderTop: `1px solid ${ui.line}`, paddingTop: 10 }}>
                                {backlog.count === 0 ? (
                                    <p style={{ margin: 0, color: '#1a7a40', fontSize: '0.86rem' }}>✓ {locale === 'en' ? 'No non-hedged offers since last shutdown.' : 'Nema nehedgiranih ponuda od zadnjeg gašenja.'}</p>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                                            <strong style={{ color: '#c0264a' }}>{locale === 'en' ? 'Non-hedged offers' : 'Nehedgirane ponude'}: {backlog.count}</strong>
                                            <span style={{ fontSize: '0.78rem', color: ui.muted }}>{locale === 'en' ? 'since' : 'od'} {formatDate(backlog.since, locale)} · Σ {backlog.total_oz} oz</span>
                                            <Btn size="sm" variant="ghost" onClick={() => setSelected(new Set(selected.size === backlog.rows.length ? [] : backlog.rows.map((x: any) => Number(x.id))))}>
                                                {selected.size === backlog.rows.length ? (locale === 'en' ? 'Deselect all' : 'Odznači sve') : (locale === 'en' ? 'Select all' : 'Označi sve')}
                                            </Btn>
                                            <span style={{ marginLeft: 'auto' }}>
                                                <Btn size="sm" variant="primary" disabled={backlogBusy || selected.size === 0} onClick={hedgeSelected}>
                                                    {backlogBusy ? '…' : `📡 ${locale === 'en' ? 'Hedge selected' : 'Hedgaj odabrane'} (${selected.size})`}
                                                </Btn>
                                            </span>
                                        </div>
                                        {backlogMsg ? <p style={{ margin: '0 0 6px', fontSize: '0.82rem', color: backlogMsg.includes('Hedg') ? '#1a7a40' : '#c0264a' }}>{backlogMsg}</p> : null}
                                        <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${ui.line}`, borderRadius: ui.radiusSm }}>
                                            {backlog.rows.map((o: any) => (
                                                <label key={o.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px', borderBottom: `1px solid ${ui.line2}`, fontSize: '0.8rem', cursor: 'pointer' }}>
                                                    <input type="checkbox" checked={selected.has(Number(o.id))} onChange={(e) => {
                                                        const n = new Set(selected); if (e.target.checked) n.add(Number(o.id)); else n.delete(Number(o.id)); setSelected(n);
                                                    }} />
                                                    <span style={{ fontFamily: 'monospace', color: ui.muted, minWidth: 90 }}>{o.broj_ponude ?? '—'}</span>
                                                    <span style={{ color: ui.muted, minWidth: 96 }}>{String(o.datum_ponude ?? '').replace('T', ' ').slice(5, 16)}</span>
                                                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.naziv_artikla ?? ''} · {o.poslovnica ?? ''}</span>
                                                    <span style={{ color: ui.acc }}>{formatNumber(Number(o.fine_ukupno_g ?? 0), locale)}g</span>
                                                    <strong>{formatMoney(Number(o.iznos_sa_pdv ?? 0), locale)}</strong>
                                                </label>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        ) : null}
                    </Card>
                ) : null}

                <Card pad={18}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                        <div>
                            <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? 'Hedging summary' : 'Hedging sažetak'}</div>
                            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: ui.ink }}>{formatNumber(payload.deals_summary.total_deals, locale)}</div>
                            <div style={{ fontSize: '0.85rem', color: ui.muted }}>{locale === 'en' ? 'Deals in selected period.' : 'Broj dealova u odabranom periodu.'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: '0.85rem', color: ui.ink2 }}>
                            <span>{locale === 'en' ? 'Buy amount' : 'BUY iznos'} <strong style={{ color: ui.ink }}>{formatMoney(payload.deals_summary.buy_amount, locale)}</strong></span>
                            <span>{locale === 'en' ? 'Sell amount' : 'SELL iznos'} <strong style={{ color: ui.ink }}>{formatMoney(payload.deals_summary.sell_amount, locale)}</strong></span>
                            <span>{locale === 'en' ? 'Net amount' : 'Neto iznos'} <strong style={{ color: ui.ink }}>{formatMoney(payload.deals_summary.net_amount, locale)}</strong></span>
                        </div>
                    </div>
                    <LineChart points={payload.deals_daily.map((row: any) => ({ date: row.date, net: row.net }))} />
                </Card>

                <Card pad={18}>
                    <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: ui.muted, fontWeight: 700 }}>{locale === 'en' ? 'Exposure (sales vs hedge)' : 'Izloženost (prodaja vs hedge)'}</div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: payload.exposure.gap_amount >= 0 ? '#1a7a40' : '#c0264a' }}>{exposureSign}{formatMoney(Math.abs(payload.exposure.gap_amount), locale)}</div>
                    <div style={{ fontSize: '0.85rem', color: ui.muted, marginBottom: 14 }}>{locale === 'en' ? 'Target is 100% coverage. Left side is underhedged, right side overhedged.' : 'Cilj je 100% pokrivenost. Lijevo je underhedged, desno overhedged.'}</div>
                    <div className="coverage-heatmap" role="img" aria-label="Coverage heatmap">
                        <div className="coverage-heatmap-grid" />
                        <span className="coverage-heatmap-needle" style={{ left: `${coverageNeedle}%` }} />
                        <div className="coverage-heatmap-scale"><span>0%</span><span>100%</span><span>200%</span></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 14, marginTop: 16 }}>
                        <StatCard icon="🏬" color="#6366f1" value={formatMoney(payload.exposure.ado_sales_amount, locale)} label={locale === 'en' ? 'ADO sales analytics' : 'ADO promet (prodajna analitika)'} />
                        <StatCard icon="🌍" color="#0ea5e9" value={formatMoney(payload.exposure.slo_bg_sales_amount, locale)} label={locale === 'en' ? 'SLO+BG sales' : 'SLO+BUG promet'} />
                        <StatCard icon="📡" color="#10b981" value={formatMoney(payload.exposure.hedged_amount, locale)} label={locale === 'en' ? 'Hedged (BUY only)' : 'Hedgirano (samo BUY)'} />
                        <StatCard icon="🎯" color={coveragePct >= 100 ? '#10b981' : '#f59e0b'} value={`${formatNumber(payload.exposure.coverage_pct, locale)}%`} label={locale === 'en' ? 'Coverage' : 'Pokrivenost'} />
                    </div>
                </Card>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 16 }}>
                    <Card pad={18}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, marginBottom: 14 }}>{locale === 'en' ? 'Net P/L by month' : 'Net P/L po mjesecu'}</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {monthPl.map((row: any) => (
                                <div key={`pl-${row.label}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <strong style={{ minWidth: 64, color: ui.ink2, fontSize: '0.82rem' }}>{row.label}</strong>
                                    <div style={{ flex: 1, height: 10, borderRadius: 999, background: ui.line2, overflow: 'hidden' }}>
                                        <span style={{ display: 'block', height: '100%', borderRadius: 999, background: row.net >= 0 ? '#1a7a40' : '#c0264a', width: `${Math.max(6, Math.round((Math.abs(row.net) / maxAbsPl) * 100))}%` }} />
                                    </div>
                                    <strong style={{ minWidth: 96, textAlign: 'right', color: ui.ink }}>{formatMoney(row.net, locale)}</strong>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card pad={18}>
                        <div style={{ fontSize: '1.02rem', fontWeight: 800, color: ui.ink, marginBottom: 14 }}>{locale === 'en' ? 'XAU oz in portfolio (start/end)' : 'XAU oz u portfelju (start/kraj)'}</div>
                        <LineChart points={ozStart} projection={ozEnd} />
                    </Card>
                </div>

                <Card pad={0}>
                    <div style={{ padding: '14px 16px 0', fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>{locale === 'en' ? 'Hedging monthly book (BUY/SELL)' : 'Hedging po mjesecima (BUY/SELL)'}</div>
                    <DataTable cols={monthlyCols} rows={monthlyRows.map((r) => ({ ...r, id: r.period }))} empty={locale === 'en' ? 'No data.' : 'Nema podataka.'} />
                </Card>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px,1fr))', gap: 16 }}>
                    <Card pad={0}>
                        <div style={{ padding: '14px 16px 0', fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>{locale === 'en' ? 'Top daily losses (portfolio change)' : 'Top dnevni gubitaka (promjena portfelja)'}</div>
                        <DataTable cols={dayCols('neg')} rows={topLossDays.map((r: any) => ({ ...r, id: `loss-${r.date}` }))} empty={locale === 'en' ? 'No data.' : 'Nema podataka.'} />
                    </Card>
                    <Card pad={0}>
                        <div style={{ padding: '14px 16px 0', fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>{locale === 'en' ? 'Top daily gains (portfolio change)' : 'Top dnevni dobitaka (promjena portfelja)'}</div>
                        <DataTable cols={dayCols('pos')} rows={topGainDays.map((r: any) => ({ ...r, id: `gain-${r.date}` }))} empty={locale === 'en' ? 'No data.' : 'Nema podataka.'} />
                    </Card>
                </div>

                <Card pad={0}>
                    <div style={{ padding: '14px 16px 0', fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>{locale === 'en' ? 'Deals by pair' : 'Poslovi po valutama/paru'}</div>
                    <DataTable cols={pairCols} rows={pairRows.map((r) => ({ ...r, id: r.pair }))} empty={locale === 'en' ? 'No data.' : 'Nema podataka.'} />
                </Card>

                <Card pad={0}>
                    <div style={{ padding: '14px 16px 0', fontSize: '1.02rem', fontWeight: 800, color: ui.ink }}>{locale === 'en' ? 'Recent deals' : 'Zadnji dealovi'}</div>
                    <DataTable cols={recentCols} rows={recent.map((d: any) => ({ ...d, id: `${d.doc}-${d.date}-${d.type}` }))} empty={locale === 'en' ? 'No data.' : 'Nema podataka.'} />
                </Card>
            </Page>
        );
    }

    return (
        <section className="module-stack">
            {payload.automation?.can_toggle ? (
                <section className="card" style={{ borderLeft: `4px solid ${autoOn ? '#1f7a36' : '#b14040'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                        <div>
                            <p className="eyebrow">{locale === 'en' ? 'Automatic hedging' : 'Automatsko hedgiranje'}</p>
                            <strong style={{ fontSize: '1.05rem', color: autoOn ? '#1f7a36' : 'var(--ad-ink)' }}>
                                {autoOn
                                    ? (locale === 'en' ? '📡 ON — auto-hedge every 15 min' : '📡 UKLJUČENO — auto-hedge svakih 15 min')
                                    : (locale === 'en' ? '✋ OFF — manual' : '✋ ISKLJUČENO — ručno')}
                            </strong>
                            {changedBy ? (
                                <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2 }}>
                                    {autoOn ? (locale === 'en' ? 'Turned on by ' : 'Uključio/la: ') : (locale === 'en' ? 'Last changed by ' : 'Zadnje mijenjao/la: ')}
                                    <strong>{changedBy}</strong>{changedAt ? ` · ${formatDate(changedAt, locale)} ${String(changedAt).replace('T', ' ').slice(11, 16)}` : ''}
                                </div>
                            ) : null}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button type="button" onClick={fetchBacklog} title={locale === 'en' ? 'Check non-hedged offers' : 'Provjeri nehedgirane ponude'}
                                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--ad-acc)', cursor: 'pointer', background: '#fffdf6', color: 'var(--ad-ink)', fontSize: '0.82rem' }}>
                                🔍 {locale === 'en' ? 'Non-hedged' : 'Nehedgirane'}
                            </button>
                            <button type="button" onClick={toggleAuto} disabled={autoToggling}
                                style={{ padding: '8px 18px', borderRadius: 8, border: 0, cursor: autoToggling ? 'wait' : 'pointer', fontWeight: 700, color: '#fff', background: autoOn ? '#b14040' : '#1f7a36' }}>
                                {autoToggling ? '…' : autoOn ? (locale === 'en' ? 'Turn OFF' : 'Isključi') : (locale === 'en' ? 'Turn ON' : 'Uključi')}
                            </button>
                        </div>
                    </div>
                    {backlog ? (
                        <div style={{ marginTop: 12, borderTop: '1px solid #eee', paddingTop: 10 }}>
                            {backlog.count === 0 ? (
                                <p style={{ margin: 0, color: '#1f7a36', fontSize: '0.86rem' }}>✓ {locale === 'en' ? 'No non-hedged offers since last shutdown.' : 'Nema nehedgiranih ponuda od zadnjeg gašenja.'}</p>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                                        <strong style={{ color: '#b14040' }}>{locale === 'en' ? 'Non-hedged offers' : 'Nehedgirane ponude'}: {backlog.count}</strong>
                                        <span style={{ fontSize: '0.78rem', color: '#666' }}>{locale === 'en' ? 'since' : 'od'} {formatDate(backlog.since, locale)} · Σ {backlog.total_oz} oz</span>
                                        <button type="button" onClick={() => setSelected(new Set(selected.size === backlog.rows.length ? [] : backlog.rows.map((x: any) => Number(x.id))))}
                                            style={{ fontSize: '0.74rem', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', padding: '2px 8px' }}>
                                            {selected.size === backlog.rows.length ? (locale === 'en' ? 'Deselect all' : 'Odznači sve') : (locale === 'en' ? 'Select all' : 'Označi sve')}
                                        </button>
                                        <button type="button" onClick={hedgeSelected} disabled={backlogBusy || selected.size === 0}
                                            style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: 0, cursor: backlogBusy ? 'wait' : 'pointer', fontWeight: 700, color: '#fff', background: selected.size ? '#1f7a36' : '#aaa' }}>
                                            {backlogBusy ? '…' : `📡 ${locale === 'en' ? 'Hedge selected' : 'Hedgaj odabrane'} (${selected.size})`}
                                        </button>
                                    </div>
                                    {backlogMsg ? <p style={{ margin: '0 0 6px', fontSize: '0.82rem', color: backlogMsg.includes('Hedg') ? '#1f7a36' : '#b14040' }}>{backlogMsg}</p> : null}
                                    <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                                        {backlog.rows.map((o: any) => (
                                            <label key={o.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px', borderBottom: '1px solid #f3f3f3', fontSize: '0.8rem', cursor: 'pointer' }}>
                                                <input type="checkbox" checked={selected.has(Number(o.id))} onChange={(e) => {
                                                    const n = new Set(selected); if (e.target.checked) n.add(Number(o.id)); else n.delete(Number(o.id)); setSelected(n);
                                                }} />
                                                <span style={{ fontFamily: 'monospace', color: '#666', minWidth: 90 }}>{o.broj_ponude ?? '—'}</span>
                                                <span style={{ color: '#999', minWidth: 96 }}>{String(o.datum_ponude ?? '').replace('T', ' ').slice(5, 16)}</span>
                                                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.naziv_artikla ?? ''} · {o.poslovnica ?? ''}</span>
                                                <span style={{ color: 'var(--ad-acc)' }}>{formatNumber(Number(o.fine_ukupno_g ?? 0), locale)}g</span>
                                                <strong>{formatMoney(Number(o.iznos_sa_pdv ?? 0), locale)}</strong>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    ) : null}
                </section>
            ) : null}
            <section className="card hero-module">
                <div className="module-heading">
                    <div>
                        <p className="eyebrow">{locale === 'en' ? 'Hedging summary' : 'Hedging sažetak'}</p>
                        <h2>{formatNumber(payload.deals_summary.total_deals, locale)}</h2>
                        <p className="muted">{locale === 'en' ? 'Deals in selected period.' : 'Broj dealova u odabranom periodu.'}</p>
                    </div>
                    <div className="metric-pair">
                        <span>{locale === 'en' ? 'Buy amount' : 'BUY iznos'} <strong>{formatMoney(payload.deals_summary.buy_amount, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Sell amount' : 'SELL iznos'} <strong>{formatMoney(payload.deals_summary.sell_amount, locale)}</strong></span>
                        <span>{locale === 'en' ? 'Net amount' : 'Neto iznos'} <strong>{formatMoney(payload.deals_summary.net_amount, locale)}</strong></span>
                    </div>
                </div>
                <LineChart points={payload.deals_daily.map((row: any) => ({ date: row.date, net: row.net }))} />
            </section>

            <section className="card exposure-card">
                <div className="exposure-layout">
                    <div className="exposure-main">
                        <div>
                            <p className="eyebrow">{locale === 'en' ? 'Exposure (sales vs hedge)' : 'Izloženost (prodaja vs hedge)'}</p>
                            <h3 className="exposure-value">
                                {exposureSign}
                                {formatMoney(Math.abs(payload.exposure.gap_amount), locale)}
                            </h3>
                            <p className="muted">{locale === 'en' ? 'Target is 100% coverage. Left side is underhedged, right side overhedged.' : 'Cilj je 100% pokrivenost. Lijevo je underhedged, desno overhedged.'}</p>
                        </div>
                        <div className="coverage-heatmap" role="img" aria-label="Coverage heatmap">
                            <div className="coverage-heatmap-grid" />
                            <span className="coverage-heatmap-needle" style={{ left: `${coverageNeedle}%` }} />
                            <div className="coverage-heatmap-scale">
                                <span>0%</span>
                                <span>100%</span>
                                <span>200%</span>
                            </div>
                        </div>
                    </div>
                    <div className="exposure-side-cards">
                        <article className="card nested-card">
                            <p className="muted">{locale === 'en' ? 'ADO sales analytics' : 'ADO promet (prodajna analitika)'}</p>
                            <strong>{formatMoney(payload.exposure.ado_sales_amount, locale)}</strong>
                        </article>
                        <article className="card nested-card">
                            <p className="muted">{locale === 'en' ? 'SLO+BG sales' : 'SLO+BUG promet'}</p>
                            <strong>{formatMoney(payload.exposure.slo_bg_sales_amount, locale)}</strong>
                        </article>
                        <article className="card nested-card">
                            <p className="muted">{locale === 'en' ? 'Hedged (BUY only)' : 'Hedgirano (samo BUY)'}</p>
                            <strong>{formatMoney(payload.exposure.hedged_amount, locale)}</strong>
                        </article>
                        <article className="card nested-card">
                            <p className="muted">{locale === 'en' ? 'Coverage' : 'Pokrivenost'}</p>
                            <strong>{formatNumber(payload.exposure.coverage_pct, locale)}%</strong>
                        </article>
                    </div>
                </div>
            </section>

            <section className="data-grid">
                <article className="card">
                    <h3>{locale === 'en' ? 'Net P/L by month' : 'Net P/L po mjesecu'}</h3>
                    <div className="list dense">
                        {monthPl.map((row: any) => (
                            <div className="list-row" key={`pl-${row.label}`}>
                                <div>
                                    <strong>{row.label}</strong>
                                    <div className="hedge-bar-track">
                                        <span className={row.net >= 0 ? 'hedge-bar positive' : 'hedge-bar negative'} style={{ width: `${Math.max(6, Math.round((Math.abs(row.net) / maxAbsPl) * 100))}%` }} />
                                    </div>
                                </div>
                                <strong>{formatMoney(row.net, locale)}</strong>
                            </div>
                        ))}
                    </div>
                </article>
                <article className="card">
                    <h3>{locale === 'en' ? 'XAU oz in portfolio (start/end)' : 'XAU oz u portfelju (start/kraj)'}</h3>
                    <LineChart points={ozStart} projection={ozEnd} />
                </article>
            </section>

            <section className="card">
                <div className="module-heading"><h3>{locale === 'en' ? 'Hedging monthly book (BUY/SELL)' : 'Hedging po mjesecima (BUY/SELL)'}</h3></div>
                <div className="table-wrap">
                    <table><thead><tr><th>Mjesec</th><th>BUY dealovi</th><th>BUY OZ</th><th>BUY EUR</th><th>SELL dealovi</th><th>SELL OZ</th><th>SELL EUR</th></tr></thead><tbody>
                        {monthlyRows.map((row) => <tr key={row.period}><td>{row.period}</td><td>{formatNumber(row.buyDeals, locale)}</td><td>{formatNumber(row.buyQty, locale)}</td><td>{formatMoney(row.buyAmount, locale)}</td><td>{formatNumber(row.sellDeals, locale)}</td><td>{formatNumber(row.sellQty, locale)}</td><td>{formatMoney(row.sellAmount, locale)}</td></tr>)}
                    </tbody></table>
                </div>
            </section>

            <section className="hedge-top-grid">
                <article className="card hedge-top-card">
                    <h3>{locale === 'en' ? 'Top daily losses (portfolio change)' : 'Top dnevni gubitaka (promjena portfelja)'}</h3>
                    <div className="table-wrap"><table><thead><tr><th>Datum</th><th>P&L €</th><th>KG</th><th>SELL €</th><th>BUY €</th></tr></thead><tbody>
                        {topLossDays.map((row: any) => (
                            <tr key={`loss-${row.date}`}>
                                <td>{formatDate(row.date, locale)}</td>
                                <td className="neg">{formatCompactMarket(row.pnl)}</td>
                                <td>{formatNumber(ozToKg(row.qty_oz), locale)}</td>
                                <td>{formatCompactMarket(row.sell_amount)}</td>
                                <td>{formatCompactMarket(row.buy_amount)}</td>
                            </tr>
                        ))}
                    </tbody></table></div>
                </article>
                <article className="card hedge-top-card">
                    <h3>{locale === 'en' ? 'Top daily gains (portfolio change)' : 'Top dnevni dobitaka (promjena portfelja)'}</h3>
                    <div className="table-wrap"><table><thead><tr><th>Datum</th><th>P&L €</th><th>KG</th><th>SELL €</th><th>BUY €</th></tr></thead><tbody>
                        {topGainDays.map((row: any) => (
                            <tr key={`gain-${row.date}`}>
                                <td>{formatDate(row.date, locale)}</td>
                                <td className="pos">{formatCompactMarket(row.pnl)}</td>
                                <td>{formatNumber(ozToKg(row.qty_oz), locale)}</td>
                                <td>{formatCompactMarket(row.sell_amount)}</td>
                                <td>{formatCompactMarket(row.buy_amount)}</td>
                            </tr>
                        ))}
                    </tbody></table></div>
                </article>
            </section>

            <section className="card">
                <div className="module-heading"><h3>{locale === 'en' ? 'Deals by pair' : 'Poslovi po valutama/paru'}</h3></div>
                <div className="table-wrap"><table><thead><tr><th>Pair</th><th>BUY cnt</th><th>BUY OZ</th><th>BUY EUR</th><th>SELL cnt</th><th>SELL OZ</th><th>SELL EUR</th></tr></thead><tbody>
                    {pairRows.map((row) => <tr key={row.pair}><td>{row.pair}</td><td>{formatNumber(row.buyCount, locale)}</td><td>{formatNumber(row.buyQty, locale)}</td><td>{formatMoney(row.buyAmount, locale)}</td><td>{formatNumber(row.sellCount, locale)}</td><td>{formatNumber(row.sellQty, locale)}</td><td>{formatMoney(row.sellAmount, locale)}</td></tr>)}
                </tbody></table></div>
            </section>

            <section className="card">
                <div className="module-heading"><h3>{locale === 'en' ? 'Recent deals' : 'Zadnji dealovi'}</h3></div>
                <div className="table-wrap"><table><thead><tr><th>{locale === 'en' ? 'Date' : 'Datum'}</th><th>{locale === 'en' ? 'Doc' : 'Dokument'}</th><th>{locale === 'en' ? 'Type' : 'Tip'}</th><th>{locale === 'en' ? 'Pair' : 'Par'}</th><th>{locale === 'en' ? 'Amount' : 'Iznos'}</th></tr></thead><tbody>
                    {recent.map((deal: any) => <tr key={`${deal.doc}-${deal.date}-${deal.type}`}><td>{formatDate(deal.date, locale)}</td><td>{deal.doc}</td><td>{deal.type}</td><td>{deal.pair}</td><td>{formatMoney(deal.amount, locale)}</td></tr>)}
                </tbody></table></div>
            </section>
        </section>
    );
}
